import React, { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';

import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import FieldDetails from './FieldDetails';
import { getFormattedDate } from '../../../lib/dates';
import { getRounded } from '../../../lib/numbers';
import {
  downloadEarlySeasonFungicidePdfs,
  downloadEarlySeasonFungicideShapefile,
} from '../../../lib/api';
import { generateCsvDownload } from '../../../lib/csv';
import { DownloadButton } from '../../../components/DownloadButton';
import { usePermissions } from '../../../hooks/use-permissions';
import { Table } from '../../../components/table/Table';
import { TableStatus } from '../../../components/table/TableStatus';
import { useGrowers } from '../../../hooks/use-growers';
import { SendToDeereButton } from '../../../components/deere/SendToDeereButton';

const PRODUCTS = [
  ['headline', 'Headline AMP®'],
  ['priaxor', 'Priaxor®'],
  ['trivapro', 'Trivapro®'],
  ['delaro', 'Delaro Complete®'],
  ['aproachPrima', 'Aproach® Prima'],
  ['other', 'Other'],
];

export default function EarlySeasonFungicideTable(props) {
  const { fields, selectedFields, reloadFields, loading } = props;
  const { selectedId: growerId } = useGrowers();
  const [showStats, setShowStats] = useState(false);
  const [statsField, setStatsField] = useState(null);
  const permissions = usePermissions();

  return (
    <div style={{ height: '100%' }}>
      <Table
        loading={loading}
        fields={fields || []}
        columns={buildColumns()}
        actions={buildActions()}
      />
      {showStats && statsField && (
        <FieldDetails
          field={statsField}
          handleClose={hideStats}
          reloadFields={reloadFields}
        />
      )}
    </div>
  );

  function buildActions() {
    return [
      permissions.includes('DOWNLOAD_RX') && (
        <DownloadButton
          key="download"
          data-testid="download-shapefiles"
          disabled={!getDownloadableAaids().length}
          download={downloadShapefiles}
          keep-open="true"
        >
          Download
        </DownloadButton>
      ),
      <DownloadButton key="csv" download={downloadCsv}>
        Export CSV
      </DownloadButton>,
      <DownloadButton
        key="pdf"
        disabled={!getDownloadableAaids().length}
        download={downloadPdfs}
        keep-open="true"
      >
        Print to PDF
      </DownloadButton>,
      <SendToDeereButton
        type="fungicide"
        pattern="{fieldName}_{product}_{year}"
        prescriptions={getDownloadablePrescriptions()}
        prescriptionMapper={(options, rx) =>
          rx.products.map((product) => ({ ...rx, id: product, product }))
        }
      />,
    ].filter(Boolean);
  }

  function hideStats() {
    setShowStats(false);
    setStatsField(null);
  }

  function downloadShapefiles() {
    return downloadEarlySeasonFungicideShapefile(
      growerId,
      getDownloadableAaids(),
    );
  }

  function onlyEnrolledWithStats(selectedField) {
    const foundField = fields?.find(
      (f) => f.aaManagedId === selectedField.aaManagedId,
    );

    return foundField?.stats;
  }

  function getDownloadableAaids() {
    return selectedFields
      .filter(onlyEnrolledWithStats)
      .map((f) => f.aaManagedId);
  }

  function getDownloadablePrescriptions() {
    const aaids = getDownloadableAaids();
    return fields?.filter((f) => aaids.includes(f.aaManagedId));
  }

  function downloadCsv() {
    const filename = `early-season-fungicide-export-${growerId}.csv`;
    const csvArr =
      selectedFields && selectedFields.length > 0
        ? fields.filter((f) =>
            selectedFields.map((s) => s.aaManagedId).includes(f.aaManagedId),
          )
        : fields;

    const csvData = csvArr.map((f) => ({
      grower: f.growerOperationName,
      farm_name: f.farmName,
      field_name: f.fieldName,
      field_id: f.fieldId,
      acres: getRounded(f.acres, 2),
      aaid: f.aaManagedId,
      headline_created: getFormattedDate(f.headlineCreated),
      headline_status: formatStatus(f.headlineStatus),
      headline_avg: f.headlineAvg,
      headline_min: f.headlineMin,
      headline_max: f.headlineMax,
      headline_total: f.headlineTotal,
      priaxor_created: getFormattedDate(f.priaxorCreated),
      priaxor_status: formatStatus(f.priaxorStatus),
      priaxor_avg: f.priaxorAvg,
      priaxor_min: f.priaxorMin,
      priaxor_max: f.priaxorMax,
      priaxor_total: f.priaxorTotal,
      trivapro_created: getFormattedDate(f.trivaproCreated),
      trivapro_status: formatStatus(f.trivaproStatus),
      trivapro_avg: f.trivaproAvg,
      trivapro_min: f.trivaproMin,
      trivapro_max: f.trivaproMax,
      trivapro_total: f.trivaproTotal,
      delaro_created: getFormattedDate(f.delaroCreated),
      delaro_status: formatStatus(f.delaroStatus),
      delaro_avg: f.delaroAvg,
      delaro_min: f.delaroMin,
      delaro_max: f.delaroMax,
      delaro_total: f.delaroTotal,
      aproachPrima_created: getFormattedDate(f.aproachPrimaCreated),
      aproachPrima_status: formatStatus(f.aproachPrimaStatus),
      aproachPrima_avg: f.aproachPrimaAvg,
      aproachPrima_min: f.aproachPrimaMin,
      aproachPrima_max: f.aproachPrimaMax,
      aproachPrima_total: f.aproachPrimaTotal,
      other_created: getFormattedDate(f.otherCreated),
      other_status: formatStatus(f.otherStatus),
      other_avg: f.otherAvg,
      other_min: f.otherMin,
      other_max: f.otherMax,
      other_total: f.otherTotal,
    }));

    generateCsvDownload(csvData, filename);
  }

  function downloadPdfs() {
    return downloadEarlySeasonFungicidePdfs(growerId, getDownloadableAaids());
  }

  function onAdjustClick(event, field) {
    event.stopPropagation();
    event.preventDefault();
    setStatsField(field);
    setShowStats(true);
  }

  function formatStatus(status) {
    if (/running/i.test(status)) return 'Running';

    if (/queued/i.test(status)) return 'Queued';

    if (/complete/i.test(status)) return 'Complete';

    if (/failed/i.test(status)) return 'Failed';

    return '--';
  }

  function buildColumns() {
    return [
      {
        field: 'plannedCrop',
        headerName: 'Planned Crop',
        flex: 0.5,
        minWidth: 75,
        align: 'center',
      },
      ...PRODUCTS.map(([id, name]) => ({
        field: `${id}Avg`,
        headerName: `${name} Avg`,
        align: 'right',
        headerAlign: 'right',
        flex: 1,
        type: 'string',
        renderCell: ({ row }) => {
          const status = row[`${id}Status`] || '--';
          const average = row[`${id}Avg`];
          return (
            <TableStatus
              status={/complete/i.test(status) ? average : formatStatus(status)}
            />
          );
        },
      })),
      {
        field: 'created',
        headerName: 'Created',
        flex: 1,
        type: 'date',
        valueFormatter: ({ value }) =>
          value ? new Date(value).toLocaleDateString() : 'N/A',
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Adjust',
        getActions: ({ row }) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Adjust"
            onClick={(e) => onAdjustClick(e, row)}
            disabled={!row.stats}
            data-testid={`adjust-${row.aaManagedId}`}
          />,
        ],
      },
    ];
  }
}
